import {getRelevantLocations} from './buildCreateOrderPayload'
import {getPriceForCountry} from '../../../../features/CatalogGrid/utils/getPriceForCountry'
import {resolveCartItemQuantity} from '../../../../features/CatalogGrid/utils/buildSendLinkSubscriptionGroups'
import {normalizeKeapPartnerId} from '../../../../utils/resolveKeapPartnerId'

const SIGNATURE_ONBOARDING_CODES = new Set([
  'mc_prov2-5-thryv-m2m',
  'signature-5-thryv-annual',
  'signature_plus-5-thryv-m2m',
  'signature_plus-5-thryv-annual',
])

const toCustomFields = item => {
  const code = String(item.productCode || '')
  const onboarding =
    item.configuredOptions?.onboarding ?? item.configuration?.onboarding ?? ''
  return {
    ...(code.startsWith('mc_')
      ? {mcDomain: item.configuredOptions?.domain ?? ''}
      : {}),
    ...(SIGNATURE_ONBOARDING_CODES.has(code)
      ? {
          includeOnboarding: {
            value: String(onboarding).toLowerCase() === 'yes' ? 'Yes' : 'No',
          },
        }
      : {}),
  }
}

const toPartnerItem = item => ({
  code: item.productCode,
  quantity: resolveCartItemQuantity(item),
  customFields: toCustomFields(item),
  ...(item.addons?.length
    ? {
        addons: item.addons
          .filter(addon => addon.productCode)
          .map(addon => ({
            code: addon.productCode,
            quantity: addon.quantity ?? 1,
          })),
      }
    : {}),
})

// On an upgrade the cart also carries the centers the account already owns
// (e.g. Keap Max Partners). The portal can't tell them from new purchases and
// would charge them again, so flag them isAlreadyAdded on the persisted
// send-link payload; ooecp zeroes flagged plans out of Due Today like core.
// Apps are left alone: an owned app may carry a quantity/tier upgrade.
export function markOwnedCenters(
  payload,
  {locations, orderMode, selectedLocationId},
) {
  const ownedKeys = new Set(
    getRelevantLocations({locations, orderMode, selectedLocationId}).flatMap(
      location =>
        (location.items ?? [])
          .filter(item => item.productType === 'CENTER' && item.alreadyIncluded)
          .map(item => `${location.thryvId}|${item.productCode}`),
    ),
  )
  if (!ownedKeys.size) return payload
  const mark = sub =>
    ownedKeys.has(`${sub.locationThryvId}|${sub.code}`)
      ? {...sub, isAlreadyAdded: true}
      : sub
  return {
    ...payload,
    parentSubscriptions: payload.parentSubscriptions.map(mark),
    childSubscriptions: payload.childSubscriptions.map(mark),
  }
}

// The legacy KP generator persists a deliberately simple cart snapshot:
// centers in planCode, apps in addOnCode, and one-time fees separately. Keep
// that exact shape instead of sending Core Catalog's UI-only item objects.
export function buildPartnerGenerateLinkData({
  locations,
  orderMode,
  selectedLocationId,
  orderId,
  country,
  keapPartnerId,
  partnerEmail,
  productConfigInfo = {},
  couponCodes = [],
}) {
  const items = getRelevantLocations({
    locations,
    orderMode,
    selectedLocationId,
  }).flatMap(location => location.items ?? [])

  const productByCode = new Map(
    [
      ...(productConfigInfo.centers ?? []),
      ...(productConfigInfo.apps ?? []),
      ...(productConfigInfo.oneTimeFees ?? []),
    ].map(product => [product.code, product]),
  )
  const oneTimeFees = items
    .filter(item => item.productType === 'ONE_TIME_FEE')
    .map(item => {
      const configuredProduct = productByCode.get(item.productCode)
      const unitAmount =
        item.unitAmount ??
        item.unitPrice ??
        item.price ??
        item.dueTodayAmount ??
        getPriceForCountry(configuredProduct?.currencies, country)
      return {...toPartnerItem(item), unitAmount}
    })

  return {
    keapPartnerId: normalizeKeapPartnerId(keapPartnerId),
    partnerEmail: partnerEmail || undefined,
    countryCode: country || 'US',
    orderHeaderId: Number(orderId),
    planCode: items
      .filter(item => item.productType === 'CENTER')
      .map(toPartnerItem),
    addOnCode: items
      .filter(item => item.productType === 'APP')
      .map(toPartnerItem),
    oneTimeFees,
    ...(couponCodes.length ? {couponCode: couponCodes} : {}),
  }
}
